import React from 'react';

/**
 * One question, one answer, as a radiogroup.
 *
 * Lifted verbatim out of InterviewFeedbackModal, which had already grown two
 * copies of this markup and was about to grow two more (the compensation and
 * would-engage-again steps) plus a fifth on the recruiter's record-conversation
 * sheet. The comment on the second copy said the extraction "was considered and
 * deferred"; five is where deferring stops paying.
 *
 * BEHAVIOUR IS UNCHANGED ON PURPOSE, so both callers stay identical:
 *
 *  - ROVING TABINDEX. The group is ONE tab stop and arrow keys move within it.
 *    A list of plain buttons is N tab stops and reads as N unrelated toggles.
 *  - SELECTION IS NEVER COLOUR ALONE. Border, wash, weight AND a filled dot,
 *    plus `aria-checked`, so the state survives a reader who cannot see teal.
 *    The dot carries `data-choice-dot`, which is how a test can assert the
 *    non-colour half exists rather than trusting a hex value.
 *  - CHOOSING IS NOT COMMITTING. This component owns no submit and fires no
 *    request; it reports the new value and stops. Every caller here writes an
 *    immutable row, so a stray arrow key must never file one.
 *
 * `consequence` is what an answer DOES, rendered only once it is selected. It
 * is optional per option: a question whose answers all do the same thing says
 * so once, above or below the group, rather than four times inside it.
 *
 * @param {string} name         id prefix, unique within the dialog
 * @param {string} [question]   the question, rendered above the group and used
 *   as its accessible name. Omit it (and pass `labelledBy`) when a heading
 *   already asks the question.
 * @param {string} [labelledBy] id of an existing element naming this group
 * @param {Array<{value, label, consequence?}>} options
 * @param {string|null} value   the selected option's value
 * @param {function} onChange   (value) => void
 * @param {object} [refs]       a useRef({current: []}) the caller can focus
 *   through; the group writes each button into it by index
 * @param {boolean} [disabled]
 */
const TEAL = '#14B8A6';
const TEAL_DEEP = '#0F766E';
const TEAL_WASH = '#F0FDFA';
const GRAY_LINE = '#E2E8F0';
const GRAY_DOT = '#CBD5E1';

export default function ChoiceGroup({
  name,
  question = null,
  labelledBy = null,
  options = [],
  value = null,
  onChange,
  refs = null,
  disabled = false,
}) {
  const localRefs = React.useRef([]);
  const buttonRefs = refs ?? localRefs;
  const questionId = `${name}-question`;
  const selected = options.find((o) => o.value === value) ?? null;

  const onKeyDown = (e) => {
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!forward && !back) return;
    if (!options.length) return;
    e.preventDefault();

    const dir = forward ? 1 : -1;
    const current = options.findIndex((o) => o.value === value);
    const from = current === -1 ? (forward ? -1 : 0) : current;
    const next = (from + dir + options.length) % options.length;

    onChange?.(options[next].value);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div>
      {question && (
        <p
          id={questionId}
          className="cv-text-sm"
          style={{ color: '#1e293b', fontWeight: 600, margin: '0 0 6px' }}
        >
          {question}
        </p>
      )}
      <div
        role="radiogroup"
        aria-labelledby={labelledBy ?? (question ? questionId : undefined)}
        onKeyDown={onKeyDown}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          margin: 0,
        }}
      >
        {options.map((option, idx) => {
          const isOn = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isOn}
              tabIndex={isOn || (!value && idx === 0) ? 0 : -1}
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              disabled={disabled}
              onClick={() => onChange?.(option.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                padding: '11px 14px',
                borderRadius: 8,
                border: `1px solid ${isOn ? TEAL : GRAY_LINE}`,
                boxShadow: isOn ? `0 0 0 1px ${TEAL}` : 'none',
                background: isOn ? TEAL_WASH : '#fff',
                color: isOn ? TEAL_DEEP : '#334155',
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: isOn ? 600 : 500,
                lineHeight: 1.4,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 120ms ease, background 120ms ease',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `2px solid ${isOn ? TEAL : GRAY_DOT}`,
                  background: '#fff',
                }}
              >
                {isOn && (
                  <span
                    data-choice-dot="on"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: TEAL,
                    }}
                  />
                )}
              </span>
              {option.label}
            </button>
          );
        })}
      </div>
      {selected?.consequence && (
        <p
          className="cv-text-sm"
          style={{ color: TEAL_DEEP, lineHeight: 1.5, margin: '6px 0 0' }}
        >
          {selected.consequence}
        </p>
      )}
    </div>
  );
}
